import styled from 'styled-components'
import { BaseMaskedInput } from 'components/Inputs/Base'
import { LookButton, iconButtonLook } from 'components/Buttons/look'
import { Link } from 'react-router-dom'
import { NumericFormat } from 'react-number-format'

/*
  Identidade visual da área de banking.

  A paleta é a da plataforma — o âmbar `#FFC100 → #FF9800` como cor de marca, `#252525`
  como escuro, raio de 15px e a sombra suave dos botões. O que muda em relação ao resto
  do app é a hierarquia: aqui o conteúdo é alinhado à esquerda e o dinheiro à direita,
  como em extrato de banco, em vez de tudo centralizado.

  O âmbar entra como acento (rótulos, ícones, destaques) e não como plano de fundo: em
  áreas grandes ele compete com os valores, que são o que a tela existe para mostrar.
*/

const CARD_SHADOW = '0px 2px 10px -6px #252525'

/*
  Os cartões são levemente tingidos, e não brancos: os inputs do app são brancos, sem
  borda e com sombra quase imperceptível, então dentro de um cartão branco eles sumiam.
  Com a superfície tingida, o campo branco volta a ler como campo — e sem precisar mexer
  nos componentes de input, que são compartilhados por mais de cem telas.
*/
const SURFACE = '#F8F8F7'
const SURFACE_LINE = '#E9E9E6'
const PAGE_WIDTH = 720

/* Envolve a página inteira, incluindo a navbar e as abas. */
export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 16px 16px 40px;
`

export const Content = styled.div`
  width: 100%;
  max-width: ${PAGE_WIDTH}px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`

export const TabStickyContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #FFFFFF;
  padding: 0px 40px;
  position: sticky;
  top: 0px;
  z-index: 3;
`

export const Card = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${SURFACE};
  border: 1px solid ${SURFACE_LINE};
  box-shadow: ${CARD_SHADOW};
  border-radius: 15px;
  padding: 16px;
  gap: ${({ gap }) => gap ?? 14}px;
`

/*
  Cartão de saldo. O fundo escuro é o mesmo gradiente do SecondaryButton, o que mantém a
  peça dentro da identidade e ao mesmo tempo separa o saldo do resto da página — é a
  primeira coisa que o titular procura ao abrir a área.
*/
export const HeroCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #252525, #3A3A3A 60%, #454545);
  box-shadow: ${CARD_SHADOW};
  border-radius: 15px;
  padding: 22px 24px;
  gap: 4px;

  /* Faixa da cor da marca, o único âmbar em área grande da tela. */
  &::before {
    content: '';
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    height: 3px;
    background: linear-gradient(to right, #FFC100, #FF9800);
  }
`

export const HeroLabel = styled.span`
  color: #FFC100;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.3px;
`

export const HeroFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #4A4A4A;
`

export const HeroDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  span {
    color: #A0A0A0;
    font-size: 12px;
  }

  b {
    color: #FFFFFF;
    font-size: 14px;
    font-weight: 500;
  }
`

export const CardHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`

export const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #252525;
  margin: 0px;
`

export const PageTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #252525;
  margin: 14px 0px 10px;
  align-self: center;
`

export const Caption = styled.p`
  font-size: 13px;
  color: #808080;
  margin: 0px;
  text-align: ${({ align }) => align || 'left'};
  line-height: 1.45;
`

/*
  Lista de pares rótulo/valor. As linhas são separadas por borda em vez de virarem
  cartões individuais: o cartão dentro do cartão engorda a página e faz cada dado
  parecer um bloco independente, quando na verdade são um conjunto só.
*/
export const DataList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

export const DataRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  padding: 11px 0px;
  border-bottom: 1px solid ${SURFACE_LINE};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0px;
  }

  &:first-child {
    padding-top: 0px;
  }
`

export const DataLabel = styled.span`
  font-size: 13px;
  color: #808080;
`

export const DataValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #252525;
  text-align: right;
  word-break: break-word;
`

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: ${({ align }) => align || 'flex-start'};
  gap: 6px;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ color }) => color || '#FF9800'};
  background-color: ${({ color }) => `${color || '#FF9800'}1A`};
`

export const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background-color: currentColor;
`

/*
  Atalhos do cartão de saldo. Existem porque a operação que o titular quer quase sempre
  já está decidida quando ele abre a área — obrigá-lo a achar a aba primeiro é um passo
  a mais sem ganho.
*/
export const QuickActionGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 10px;
`

export const QuickAction = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 14px 10px 8px;
  border: 1px solid ${SURFACE_LINE};
  border-radius: 15px;
  background-color: ${SURFACE};
  box-shadow: ${CARD_SHADOW};
  transition: border-color 0.2s, transform 0.15s;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #FFC100;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  span {
    font-size: 13px;
    font-weight: 500;
    color: #252525;
  }
`

export const QuickActionIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: linear-gradient(to bottom right, #FFC100, #FF9800);
`

/* ---- Telas de operação ---- */

/*
  Cabeçalho de uma tela de operação. Faz o papel do cabeçalho da stack dos apps: diz onde
  se está e oferece a volta, já que aqui a aba de movimentação deixou de existir.
*/
export const BackRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
`

export const BackButton = styled(LookButton).attrs(iconButtonLook({ variant: 'secondary' }))`
  width: 32px;
  height: 32px;
`

export const OperationTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #252525;
  margin: 0px;
`

/* Cada forma de pagar dentro da área Pix. A linha inteira é clicável. */
export const OptionItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 4px;
  border: none;
  border-bottom: ${({ last }) => last ? 'none' : `1px solid ${SURFACE_LINE}`};
  background: none;
  text-align: left;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`

export const OptionIcon = styled.div`
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: linear-gradient(to bottom right, #FFC100, #FF9800);
`

export const OptionTexts = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const OptionLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #252525;
`

export const OptionDescription = styled.span`
  font-size: 12px;
  color: #606060;
  line-height: 16px;
`

/* ---- Extrato ---- */

export const TransactionList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

export const DateGroupLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #808080;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 16px 0px 4px;

  &:first-child {
    margin-top: 0px;
  }
`

export const TransactionRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 12px 0px;
  border-bottom: 1px solid ${SURFACE_LINE};

  &:last-child {
    border-bottom: none;
  }
`

/*
  O ícone é tingido pela direção do dinheiro, não pela operação: verde para entrada,
  cinza para saída. É o que permite ler a coluna inteira de relance sem comparar valores.
*/
export const TransactionIcon = styled.div`
  width: 38px;
  height: 38px;
  min-width: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 19px;
  color: ${({ credit }) => (credit ? '#2FA855' : '#606060')};
  background-color: ${({ credit }) => (credit ? '#44CF6C22' : '#F0F0F0')};
`

export const TransactionInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0px;

  b {
    font-size: 14px;
    font-weight: 500;
    color: #252525;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const TransactionMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  span {
    font-size: 12px;
    color: #909090;
  }
`

export const TransactionSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`

export const EmptyState = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 20px;
  color: #B0B0B0;

  b {
    font-size: 14px;
    font-weight: 500;
    color: #606060;
  }

  p {
    font-size: 13px;
    color: #909090;
    margin: 0px;
    text-align: center;
  }
`

/* ---- Formulários ---- */

export const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${SURFACE};
  border: 1px solid ${SURFACE_LINE};
  box-shadow: ${CARD_SHADOW};
  border-radius: 15px;
  padding: 16px;
  gap: 14px;
`

/*
  `maxWidth` existe para os campos curtos: sem ele, o flex estica um CPF ou um valor até a
  largura do cartão, e o campo fica com o tamanho de um endereço para caber onze dígitos.
  Só limita — quando a linha é estreita demais, o flex encolhe abaixo do teto normalmente.
*/
export const FormField = styled.div`
  flex: ${({ stretch }) => stretch || 1};
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0px;
  max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px` : 'none')};
`

export const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #777777;
  margin-left: 12px;
`

/*
  Cabeçalho de um campo: o rótulo e, no do documento, a escolha entre CNPJ e CPF.

  A altura é fixa, e `withOptions` vale para TODOS os campos da linha — não só para o que
  tem a escolha. É ela que reserva a mesma faixa em cima de cada entrada, e é isso que as
  mantém alinhadas. A alternativa, alinhar a linha pela base, funciona até o primeiro aviso
  que quebra em duas linhas: ele estica o campo para baixo e empurra a entrada dele para
  cima, desalinhando-a das vizinhas.
*/
export const FieldHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 4px;
  min-height: ${({ withOptions }) => (withOptions ? '42px' : '18px')};
`

export const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
`

/*
  Campo com máscara, para o CPF/CNPJ da abertura da conta. Repete a aparência do TextInput
  compartilhado de propósito: os dois dividem a mesma linha do formulário, e qualquer
  diferença de altura ou de sombra apareceria.
*/
export const MaskedInput = styled(BaseMaskedInput)`
  width: 100%;
`

export const CheckRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`

export const CheckLabel = styled.label`
  font-size: 13px;
  color: #777777;
  cursor: pointer;
`

export const FormActions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: ${({ align }) => align || 'flex-end'};
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 5px;
`

/*
  Faixa de destaque do valor final de uma operação — o total do boleto, o quanto sai na
  transferência. Fica visualmente separada dos demais campos porque é o número que o
  titular confere antes de confirmar.
*/
export const HighlightRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  padding: 13px 15px;
  border-radius: 15px;
  background-color: #FFFFFF;
  border: 1px solid ${SURFACE_LINE};
`

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${SURFACE_LINE};
`

export const InfoText = styled.p`
  color: #777777;
  font-size: 13px;
  text-align: ${({ align }) => align || 'left'};
  line-height: 1.45;
  margin: 0px;
  margin-left: ${({ align }) => (align === 'center' ? '0px' : '12px')};
`

/*
  `margin-bottom` zerado porque é um `<p>`: sem isso o 1em que o navegador põe embaixo se
  soma ao `gap` do formulário, e o aviso abre um buraco entre uma linha de campos e a
  seguinte — justamente quando a tela já está mostrando um erro.
*/
export const WarningText = styled.p`
  margin-top: -3px;
  margin-bottom: 0px;
  margin-left: ${({ align }) => (align === 'center' ? '0px' : '12px')};
  text-align: ${({ align }) => align || 'left'};
  font-size: 13px;
  color: #FF3939;
`

/* ---- Onboarding ---- */

export const StatusGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
`

export const StatusCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 12px 14px;
  background-color: #FFFFFF;
  border: 1px solid ${SURFACE_LINE};
  border-radius: 15px;

  span {
    font-size: 12px;
    color: #808080;
  }
`

/*
  `highlight` marca o documento recusado. É o único estado em que o titular precisa agir de
  novo sobre algo que já mandou, e sem destaque ele se perde no meio dos outros cartões.
*/
export const DocumentCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
  padding: 14px 15px;
  background-color: ${({ highlight }) => highlight ? '#FF39390A' : '#FFFFFF'};
  border: 1px solid ${({ highlight }) => highlight ? '#FF393940' : SURFACE_LINE};
  border-radius: 15px;
`

/* Linha de apoio do documento: formatos aceitos, arquivos já enviados, avisos. */
export const DocumentMeta = styled.span`
  font-size: 12px;
  color: #A0A0A0;
  text-align: left;
  line-height: 1.4;
`

export const DocumentInfo = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  b {
    font-size: 14px;
    font-weight: 500;
    color: #252525;
  }

  p {
    margin: 0px;
    font-size: 13px;
    color: #808080;
    text-align: left;
    line-height: 1.4;
  }
`

export const MissingFieldGrid = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`

export const MissingFieldChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 13px;
  color: #C23A3A;
  background-color: #FF39391A;
`

/* ---- Passo a passo do onboarding ---- */

export const StepList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const Step = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0px;

  div.marker {
    width: 24px;
    height: 24px;
    min-width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    color: ${({ done }) => (done ? '#FFFFFF' : '#808080')};
    background: ${({ done, current }) => done
      ? 'linear-gradient(to bottom right, #44CF6C, #2FA855)'
      : current ? 'linear-gradient(to bottom right, #FFC100, #FF9800)' : '#EEEEEE'};
  }

  div.text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  b {
    font-size: 14px;
    font-weight: 500;
    color: ${({ done, current }) => (done || current ? '#252525' : '#909090')};
  }

  p {
    margin: 0px;
    font-size: 12px;
    color: #909090;
  }
`

export const ExternalLinkButton = styled.a.attrs ({
  target: '_blank',
  rel: 'noopener noreferrer'
})`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 38px;
  background-color: #252525;
  color: #FFFFFF;
  font-size: 14px;
  padding: 0px 18px;
  border-radius: 15px;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: ${CARD_SHADOW};
  transition: opacity 0.2s;
  gap: 8px;

  &:hover {
    opacity: 0.7;
    color: #FFFFFF;
  }
`

export const LinkButton = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 38px;
  padding: 0px 22px;
  border-radius: 15px;
  font-size: 14px;
  color: #000000;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: ${CARD_SHADOW};
  background: linear-gradient(to right, #FFC100, #FF9800);
  transition: opacity 0.2s;
  gap: 8px;

  &:hover {
    opacity: 0.7;
    color: #000000;
  }
`

/* ---- Dinheiro ---- */

const moneyFormat = props => ({
  value: Number (props.value).toFixed (2).replace ('.', ','),
  thousandSeparator: '.',
  decimalSeparator: ',',
  displayType: 'text',
  prefix: 'R$ '
})

export const MoneyTextMask = styled(NumericFormat).attrs (moneyFormat)`
  color: #252525;
  font-size: 14px;
  font-weight: 500;
`

/*
  Valor de uma movimentação, com o sinal explícito. Verde só para entrada: pintar as
  saídas de vermelho faria um extrato normal parecer uma lista de erros.
*/
export const AmountText = styled(NumericFormat).attrs (props => ({
  ...moneyFormat (props),
  prefix: `${props.credit ? '+' : '−'} R$ `
}))`
  color: ${({ credit }) => (credit ? '#2FA855' : '#252525')};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
`

export const PlainMoneyText = styled(NumericFormat).attrs (moneyFormat)`
  color: #252525;
  font-size: 15px;
  font-weight: 600;
`

export const HeroBalance = styled(NumericFormat).attrs (moneyFormat)`
  color: ${({ value }) => (parseFloat (String (value).replace (',', '.')) < 0 ? '#FF7B7B' : '#FFFFFF')};
  font-size: 34px;
  font-weight: 600;
  letter-spacing: -0.5px;
  line-height: 1.2;
`
